import { supabase } from 'src/lib/supabaseClient';
import { teamsService, TeamsSessionData } from 'src/services/teamsServiceBackend';

export type SessionStatus = 'scheduled' | 'started' | 'ended' | 'cancelled' | 'failed';
export type SessionMode = 'video' | 'audio' | 'quiz' | 'hybrid';
export type SessionType = 'regular' | 'demo' | 'makeup' | 'assessment' | 'consultation';
export type SessionSchedulingType = 'on_demand' | 'recurring';
export type SessionApprovalStatus = 'pending' | 'approved' | 'rejected';
export type SessionCreatedBy = 'admin' | 'tutor' | 'student';
export type SessionParticipantRole = 'tutor' | 'student' | 'co_host' | 'observer';

export interface SessionParticipantWriteData {
  user_id: string;
  participant_role: SessionParticipantRole;
  added_by?: string | null;
}

export interface SessionParticipantData {
  id: string;
  session_id: string;
  user_id: string;
  participant_role: SessionParticipantRole;
  participation_status: 'active' | 'removed';
  added_by?: string | null;
  created_at: string;
  updated_at: string;
  profile?: {
    first_name: string;
    last_name: string;
    email: string;
    profile_picture_url?: string | null;
  };
}

export interface RecurringPattern {
  frequency?: 'daily' | 'weekly' | 'monthly';
  interval?: number;
  days_of_week?: number[];
  count?: number;
  until?: string;
  timezone?: string;
  [key: string]: any;
}

export interface SessionDetailsWriteData {
  description?: string;
  materials?: any;
  recording_url?: string;
  student_attended?: boolean;
  tutor_attended?: boolean;
  start_time?: string;
  end_time?: string;
  actual_start_time?: string;
  actual_end_time?: string;
  tutor_talk_time?: number;
  student_talk_time?: number;
  whiteboard_interactions?: number;
  messages_sent?: number;
  messages_received?: number;
  participation_score?: number;
  cancellation_reason?: string;
  notes?: string;
  meeting_metadata?: any;
  participation_level?: 'Highly Engaged' | 'Moderate' | 'Passive' | 'No Participation';
  student_joined_at?: string;
  student_left_at?: string;
  tutor_joined_at?: string;
  tutor_left_at?: string;
}

export interface MeetingSessionWriteData {
  provider_id: string;
  provider_meeting_id?: string;
  meeting_url: string;
  join_url?: string;
  moderator_url?: string;
  meeting_settings?: any;
  security_settings?: any;
  meeting_status?: SessionStatus;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  actual_start_time?: string;
  actual_end_time?: string;
  recording_enabled?: boolean;
  recording_url?: string;
  recording_status?: 'not_started' | 'recording' | 'processing' | 'ready' | 'failed';
  max_participants_reached?: number;
  total_join_attempts?: number;
  provider_response?: any;
  last_sync_at?: string;
}

export interface SessionWriteInput {
  batch_id?: string | null;
  curriculum_unit_id?: string | null;
  curriculum_objective_id?: string | null;
  tutor_id: string;
  student_id?: string | null;
  scheduled_at: string;
  duration_min: number;
  status?: SessionStatus;
  mode?: SessionMode;
  session_type?: SessionType;
  meeting_url?: string | null;
  location?: string | null;
  has_conflict?: boolean;
  urgency_level?: number | null;
  rescheduled_from?: string | null;
  created_by: SessionCreatedBy;
  meeting_provider_id?: string | null;
  scheduling_type?: SessionSchedulingType;
  approval_status?: SessionApprovalStatus;
  requested_by?: string | null;
  approved_by?: string | null;
  original_tutor_id?: string | null;
  reschedule_count?: number;
  cancellation_reason?: string | null;
  last_updated_by?: string | null;
  recurring_pattern?: RecurringPattern | null;
  parent_session_id?: string | null;
  /**
   * The canonical session roster. When omitted, the tutor and optional student
   * scalar assignments are converted to the default 1:1 roster.
   */
  participants?: SessionParticipantWriteData[];
  session_details?: SessionDetailsWriteData;
  meeting_session?: MeetingSessionWriteData;
  create_teams_meeting?: boolean;
}

export interface CreateRecurringSessionsInput extends Omit<SessionWriteInput, 'scheduling_type' | 'parent_session_id' | 'recurring_pattern'> {
  recurring_pattern: RecurringPattern;
  occurrences?: string[];
  parent_session_id?: string | null;
}

export interface FetchAdminSessionsFilters {
  status?: SessionStatus;
  scheduling_type?: SessionSchedulingType;
  approval_status?: SessionApprovalStatus;
  tutor_id?: string;
  student_id?: string;
  batch_id?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface UpdateSessionInput extends Partial<Omit<SessionWriteInput, 'session_details' | 'meeting_session' | 'participants'>> {
  session_details?: SessionDetailsWriteData | null;
  meeting_session?: MeetingSessionWriteData | null;
}

export interface SessionData {
  id: string;
  batch_id?: string | null;
  entitlement_line_id?: string | null;
  curriculum_unit_id?: string | null;
  curriculum_objective_id?: string | null;
  tutor_id: string;
  student_id?: string | null;
  scheduled_at: string;
  duration_min: number;
  status: SessionStatus;
  mode: SessionMode;
  session_type: SessionType;
  meeting_url?: string | null;
  location?: string | null;
  has_conflict?: boolean;
  urgency_level?: number | null;
  rescheduled_from?: string | null;
  created_by: SessionCreatedBy;
  created_at: string;
  updated_at: string;
  meeting_provider_id?: string | null;
  scheduling_type?: SessionSchedulingType;
  approval_status?: SessionApprovalStatus;
  requested_by?: string | null;
  approved_by?: string | null;
  original_tutor_id?: string | null;
  reschedule_count?: number;
  cancellation_reason?: string | null;
  last_updated_by?: string | null;
  recurring_pattern?: RecurringPattern | null;
  parent_session_id?: string | null;
  
  // Joined data
  tutor_profile?: {
    first_name: string;
    last_name: string;
    profile_picture_url?: string;
    email: string;
    timezone?: string;
  };
  tutor_data?: {
    education?: {
      gpa?: number;
      field: string;
      degree: string;
      country: string;
      institution: string;
      year_of_completion: number;
    }[];
    hourly_rate?: number;
    subjects_taught?: string[] | string;
    experience?: any[];
    bio?: string;
    verification_status?: string;
    rating?: number;
  };
  student_profile?: {
    first_name: string;
    last_name: string;
    profile_picture_url?: string;
    email: string;
  };
  curriculum_unit?: {
    name: string;
    domain?: {
      name: string;
      subject?: {
        name: string;
        description?: string;
        icon?: string;
      };
    };
  };
  curriculum_objective?: {
    title: string;
    learning_objective?: string;
  };
  /** @deprecated Session reads now populate curriculum_unit instead. */
  topic?: {
    name: string;
    description?: string;
    icon?: string;
    subject?: { name: string; description?: string; icon?: string };
  };
  /** @deprecated Session reads now populate curriculum_objective instead. */
  subtopic?: { name: string; description?: string };
  batch?: {
    name: string;
    product_name: string;
  };
  meeting_session?: {
    meeting_url: string;
    join_url?: string;
    meeting_status: string;
    recording_enabled?: boolean;
    provider_meeting_id?: string;
  };
  meeting_provider?: {
    name: string;
    display_name: string;
  };
  session_feedback?: {
    rating?: number;
    comments?: string;
  }[];
  session_details?: {
    description?: string;
    participation_score?: number;
    participation_level?: string;
    notes?: string;
    materials?: string[];
    student_joined_at?: string;
    student_left_at?: string;
    tutor_joined_at?: string;
    tutor_left_at?: string;
  };
}

export interface SessionStats {
  totalSessions: number;
  totalHours: number;
  completedSessions: number;
  upcomingSessions: number;
  averageRating: number;
  favoriteTopics: string[];
  favoriteTutors: string[];
}

class SessionsService {
  private readonly sessionSelect = `
    *,
    tutor_profile:profiles!tutor_id(
      first_name,
      last_name,
      profile_picture_url,
      email,
      timezone
    ),
    student_profile:profiles!student_id(
      first_name,
      last_name,
      profile_picture_url,
      email
    ),
    curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
      name,
      domain:curriculum_domains(
        name,
        subject:subjects(
          name,
          description,
          icon
        )
      )
    ),
    curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
      title,
      learning_objective
    ),
    batch:batches(
      name,
      product_name
    ),
    meeting_session:meeting_sessions(
      meeting_url,
      join_url,
      meeting_status,
      recording_enabled,
      provider_meeting_id
    ),
    meeting_provider:meeting_providers(
      name,
      display_name
    ),
    session_feedback(
      rating,
      comments
    ),
    session_details(
      description,
      participation_score,
      participation_level,
      notes,
      materials,
      student_joined_at,
      student_left_at,
      tutor_joined_at,
      tutor_left_at,
      meeting_metadata
    )
  `;

  private buildSessionInsertPayload(input: SessionWriteInput) {
    const {
      participants,
      session_details,
      meeting_session,
      create_teams_meeting,
      ...sessionData
    } = input;

    return {
      status: 'scheduled' as SessionStatus,
      mode: 'video' as SessionMode,
      session_type: 'regular' as SessionType,
      approval_status: 'approved' as SessionApprovalStatus,
      ...sessionData
    };
  }

  private resolveSessionParticipants(
    input: Pick<SessionWriteInput, 'tutor_id' | 'student_id' | 'participants'>
  ): SessionParticipantWriteData[] {
    const participants = input.participants?.length
      ? input.participants
      : [
          { user_id: input.tutor_id, participant_role: 'tutor' as const },
          ...(input.student_id
            ? [{ user_id: input.student_id, participant_role: 'student' as const }]
            : [])
        ];

    const activeUserIds = new Set<string>();
    for (const participant of participants) {
      if (!participant.user_id) {
        throw new Error('Each session participant must have a user ID.');
      }

      if (activeUserIds.has(participant.user_id)) {
        throw new Error('A user can only have one active role in a session roster.');
      }

      activeUserIds.add(participant.user_id);
    }

    const tutorAssignment = participants.find(
      (participant) => participant.user_id === input.tutor_id && participant.participant_role === 'tutor'
    );

    if (!tutorAssignment) {
      throw new Error('The assigned session tutor must be included in the roster with the tutor role.');
    }

    if (input.student_id) {
      const studentAssignment = participants.find(
        (participant) => participant.user_id === input.student_id && participant.participant_role === 'student'
      );

      if (!studentAssignment) {
        throw new Error('The assigned session student must be included in the roster with the student role.');
      }
    }

    return participants;
  }

  private buildSessionParticipantRows(
    sessionId: string,
    participants: SessionParticipantWriteData[]
  ) {
    return participants.map((participant) => ({
      session_id: sessionId,
      user_id: participant.user_id,
      participant_role: participant.participant_role,
      participation_status: 'active',
      added_by: participant.added_by ?? null
    }));
  }

  private async insertSessionParticipants(
    sessionId: string,
    participants: SessionParticipantWriteData[]
  ): Promise<void> {
    const rows = this.buildSessionParticipantRows(sessionId, participants);

    const { error } = await supabase
      .from('session_participants')
      .insert(rows);

    if (error) {
      console.error('Error writing session participants:', error);
      throw new Error(`Failed to write session participants: ${error.message}`);
    }
  }

  private async getRosterSessionContext(sessionId: string): Promise<{
    id: string;
    tutor_id: string;
    student_id?: string | null;
    status: SessionStatus;
  }> {
    const { data, error } = await supabase
      .from('sessions')
      .select('id, tutor_id, student_id, status')
      .eq('id', sessionId)
      .single();

    if (error || !data) {
      throw new Error(error?.message || 'Session not found.');
    }

    if (data.status !== 'scheduled') {
      throw new Error('Participants can only be changed while a session is scheduled.');
    }

    return data;
  }

  private async getCurrentUserId(): Promise<string> {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error || !user) {
      throw new Error('You must be signed in to manage session participants.');
    }

    return user.id;
  }

  private normalizeRosterParticipants(
    context: Pick<SessionWriteInput, 'tutor_id' | 'student_id'>,
    participants: SessionParticipantWriteData[],
    addedBy: string
  ): SessionParticipantWriteData[] {
    return this.resolveSessionParticipants({
      ...context,
      participants
    }).map((participant) => ({
      user_id: participant.user_id,
      participant_role: participant.participant_role,
      added_by: addedBy
    }));
  }

  /**
   * Read the canonical roster. RLS restricts this to administrators and each
   * participant's own active membership until the participant-facing summary API
   * is introduced.
   */
  async getSessionParticipants(
    sessionId: string,
    options: { includeRemoved?: boolean } = {}
  ): Promise<SessionParticipantData[]> {
    let query = supabase
      .from('session_participants')
      .select(`
        id,
        session_id,
        user_id,
        participant_role,
        participation_status,
        added_by,
        created_at,
        updated_at,
        profile:profiles!session_participants_user_id_fkey(
          first_name,
          last_name,
          email,
          profile_picture_url
        )
      `)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (!options.includeRemoved) {
      query = query.eq('participation_status', 'active');
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching session participants:', error);
      throw new Error(`Failed to fetch session participants: ${error.message}`);
    }

    return (data || []) as SessionParticipantData[];
  }

  /** Add one active participant without changing the legacy scalar assignments. */
  async addSessionParticipant(
    sessionId: string,
    participant: Omit<SessionParticipantWriteData, 'added_by'>
  ): Promise<SessionParticipantData[]> {
    const [context, addedBy, activeParticipants] = await Promise.all([
      this.getRosterSessionContext(sessionId),
      this.getCurrentUserId(),
      this.getSessionParticipants(sessionId)
    ]);

    const normalized = this.normalizeRosterParticipants(
      context,
      [
        ...activeParticipants.map(({ user_id, participant_role }) => ({ user_id, participant_role })),
        participant
      ],
      addedBy
    );
    const target = normalized.find(({ user_id }) => user_id === participant.user_id);

    if (!target) {
      throw new Error('Participant could not be added to the session roster.');
    }

    const existing = await this.getSessionParticipants(sessionId, { includeRemoved: true });
    const existingTarget = existing.find(({ user_id }) => user_id === target.user_id);

    if (existingTarget?.participation_status === 'active') {
      throw new Error('This user is already an active session participant.');
    }

    if (existingTarget) {
      const { error } = await supabase
        .from('session_participants')
        .update({
          participant_role: target.participant_role,
          participation_status: 'active',
          added_by: addedBy
        })
        .eq('id', existingTarget.id);

      if (error) {
        throw new Error(`Failed to restore session participant: ${error.message}`);
      }
    } else {
      const { error } = await supabase
        .from('session_participants')
        .insert({
          session_id: sessionId,
          user_id: target.user_id,
          participant_role: target.participant_role,
          participation_status: 'active',
          added_by: addedBy
        });

      if (error) {
        throw new Error(`Failed to add session participant: ${error.message}`);
      }
    }

    return this.getSessionParticipants(sessionId);
  }

  /**
   * Replace the active roster as an admin operation. The primary tutor and
   * legacy student assignment must remain present until those scalar fields are
   * migrated in a later compatibility feature.
   */
  async replaceSessionParticipants(
    sessionId: string,
    participants: Omit<SessionParticipantWriteData, 'added_by'>[]
  ): Promise<SessionParticipantData[]> {
    const [context, addedBy, existing] = await Promise.all([
      this.getRosterSessionContext(sessionId),
      this.getCurrentUserId(),
      this.getSessionParticipants(sessionId, { includeRemoved: true })
    ]);
    const normalized = this.normalizeRosterParticipants(context, participants, addedBy);
    const desiredUserIds = new Set(normalized.map(({ user_id }) => user_id));

    for (const existingParticipant of existing) {
      if (
        existingParticipant.participation_status === 'active' &&
        !desiredUserIds.has(existingParticipant.user_id)
      ) {
        const { error } = await supabase
          .from('session_participants')
          .update({ participation_status: 'removed' })
          .eq('id', existingParticipant.id);

        if (error) {
          throw new Error(`Failed to remove session participant: ${error.message}`);
        }
      }
    }

    for (const participant of normalized) {
      const existingParticipant = existing.find(({ user_id }) => user_id === participant.user_id);

      if (existingParticipant) {
        const { error } = await supabase
          .from('session_participants')
          .update({
            participant_role: participant.participant_role,
            participation_status: 'active',
            added_by: addedBy
          })
          .eq('id', existingParticipant.id);

        if (error) {
          throw new Error(`Failed to update session participant: ${error.message}`);
        }
      } else {
        const { error } = await supabase
          .from('session_participants')
          .insert({
            session_id: sessionId,
            user_id: participant.user_id,
            participant_role: participant.participant_role,
            participation_status: 'active',
            added_by: addedBy
          });

        if (error) {
          throw new Error(`Failed to add session participant: ${error.message}`);
        }
      }
    }

    return this.getSessionParticipants(sessionId);
  }

  /** Soft-remove an additional participant while retaining the roster audit trail. */
  async removeSessionParticipant(sessionId: string, userId: string): Promise<SessionParticipantData[]> {
    const [context, existing] = await Promise.all([
      this.getRosterSessionContext(sessionId),
      this.getSessionParticipants(sessionId)
    ]);
    const participant = existing.find(({ user_id }) => user_id === userId);

    if (!participant) {
      throw new Error('This user is not an active session participant.');
    }

    if (participant.participant_role === 'tutor' || userId === context.student_id) {
      throw new Error('The assigned tutor or primary student must be changed through the session edit workflow.');
    }

    const { error } = await supabase
      .from('session_participants')
      .update({ participation_status: 'removed' })
      .eq('id', participant.id);

    if (error) {
      throw new Error(`Failed to remove session participant: ${error.message}`);
    }

    return this.getSessionParticipants(sessionId);
  }

  private async insertSessionRelatedRecords(
    sessionId: string,
    sessionDetails?: SessionDetailsWriteData | null,
    meetingSession?: MeetingSessionWriteData | null
  ): Promise<void> {
    if (sessionDetails) {
      const { error } = await supabase
        .from('session_details')
        .upsert({
          session_id: sessionId,
          ...sessionDetails
        }, { onConflict: 'session_id' });

      if (error) {
        console.error('Error writing session details:', error);
        throw new Error(`Failed to write session details: ${error.message}`);
      }
    }

    if (meetingSession) {
      const { error } = await supabase
        .from('meeting_sessions')
        .upsert({
          session_id: sessionId,
          meeting_status: 'scheduled',
          ...meetingSession
        }, { onConflict: 'session_id' });

      if (error) {
        console.error('Error writing meeting session:', error);
        throw new Error(`Failed to write meeting session: ${error.message}`);
      }

      const meetingUrl = meetingSession.meeting_url || meetingSession.join_url;
      if (meetingUrl) {
        const { error: sessionError } = await supabase
          .from('sessions')
          .update({ meeting_url: meetingUrl })
          .eq('id', sessionId);

        if (sessionError) {
          console.error('Error updating session meeting URL:', sessionError);
          throw new Error(`Failed to update session meeting URL: ${sessionError.message}`);
        }
      }
    }
  }

  private async getTeamsProviderId(): Promise<string> {
    const { data, error } = await supabase
      .from('meeting_providers')
      .select('id')
      .eq('name', 'microsoft_teams')
      .single();

    if (error || !data?.id) {
      throw new Error(error?.message || 'Microsoft Teams meeting provider is not configured.');
    }

    return data.id;
  }

  private buildTeamsSessionData(session: SessionData): TeamsSessionData {
    const scheduledAt = new Date(session.scheduled_at);
    const endTime = new Date(scheduledAt.getTime() + session.duration_min * 60_000);
    const subject = [
      session.curriculum_unit?.domain?.subject?.name,
      session.curriculum_unit?.name,
      session.batch?.product_name,
      session.session_type === 'demo' ? 'Demo session' : 'Tutoring session'
    ].filter(Boolean).join(' - ');
    const participants = [
      session.student_profile?.email,
      session.tutor_profile?.email
    ].filter((email): email is string => Boolean(email));

    return {
      subject: subject || 'Tutoring session',
      startTime: scheduledAt.toISOString(),
      endTime: endTime.toISOString(),
      organizerId: session.tutor_id,
      participants,
      description: session.session_details?.description || session.session_details?.notes,
      sessionType: session.session_type,
      // Every scheduled tutoring session uses the same admission policy,
      // including demo sessions: the tutor is the only participant admitted
      // automatically and students wait in the Teams lobby.
      meetingConfig: {
        organizerCanStart: true,
        studentsCanStart: false,
        studentsCanJoinEarly: true,
        useWaitingRoom: true,
        securitySettings: {
          allowAnonymousUsersToJoinMeeting: true,
          allowAnonymousUsersToStartMeeting: false,
          lobbyBypassSettings: {
            scope: 'organizer',
            isDialInBypassEnabled: false
          },
          autoAdmittedUsers: 'organizer_only',
          allowedPresenters: 'organizer'
        }
      }
    };
  }

  /**
   * Provision and persist the single Teams meeting assigned to a session.
   * Scheduling flows call this immediately after inserting the session; join
   * flows must only consume the persisted URL.
   */
  async createTeamsMeetingForSession(sessionId: string): Promise<SessionData> {
    const session = await this.fetchSessionOrThrow(sessionId);

    if (session.meeting_url || session.meeting_session?.meeting_url) {
      return session;
    }

    const providerId = await this.getTeamsProviderId();
    const meeting = await teamsService.createMeeting(this.buildTeamsSessionData(session));
    const meetingUrl =
      meeting.joinUrlVisitTeams ||
      meeting.joinUrlBrowser ||
      meeting.joinUrlDirectClean ||
      meeting.joinUrlSimple ||
      meeting.joinUrlMinimal ||
      meeting.joinUrl;

    await this.updateSession(sessionId, {
      meeting_provider_id: providerId,
      meeting_url: meetingUrl,
      meeting_session: {
        provider_id: providerId,
        provider_meeting_id: meeting.meetingId,
        meeting_url: meetingUrl,
        join_url: meeting.joinUrl,
        scheduled_start_time: session.scheduled_at,
        scheduled_end_time: new Date(
          new Date(session.scheduled_at).getTime() + session.duration_min * 60_000
        ).toISOString(),
        provider_response: meeting.meetingData,
        last_sync_at: new Date().toISOString()
      },
      session_details: {
        meeting_metadata: {
          provider: 'microsoft_teams',
          meeting_id: meeting.meetingId,
          join_url: meeting.joinUrl,
          join_url_browser: meeting.joinUrlBrowser,
          join_url_visit_teams: meeting.joinUrlVisitTeams,
          join_url_direct_clean: meeting.joinUrlDirectClean,
          join_url_simple: meeting.joinUrlSimple,
          join_url_minimal: meeting.joinUrlMinimal,
          debug_info: meeting.debugInfo
        }
      }
    });

    return this.fetchSessionOrThrow(sessionId);
  }

  private async fetchSessionOrThrow(sessionId: string): Promise<SessionData> {
    const session = await this.getSessionById(sessionId);

    if (!session) {
      throw new Error('Session was written but could not be fetched');
    }

    return session;
  }

  /**
   * Create a single on-demand session.
   */
  async createOnDemandSession(sessionInput: SessionWriteInput): Promise<SessionData> {
    try {
      const participants = this.resolveSessionParticipants(sessionInput);
      const sessionPayload = this.buildSessionInsertPayload({
        ...sessionInput,
        scheduling_type: 'on_demand',
        recurring_pattern: null,
        parent_session_id: null
      });

      const { data, error } = await supabase
        .from('sessions')
        .insert(sessionPayload)
        .select('id')
        .single();

      if (error) {
        console.error('Error creating on-demand session:', error);
        throw new Error(`Failed to create on-demand session: ${error.message}`);
      }

      await this.insertSessionParticipants(data.id, participants);

      await this.insertSessionRelatedRecords(
        data.id,
        sessionInput.session_details,
        sessionInput.meeting_session
      );

      if (sessionInput.create_teams_meeting && !sessionInput.meeting_session) {
        return await this.provisionTeamsMeetingForNewSession(data.id);
      }

      return this.fetchSessionOrThrow(data.id);
    } catch (error) {
      console.error('Error in createOnDemandSession:', error);
      throw error;
    }
  }

  /**
   * Create a recurring set. The first occurrence is the parent unless a parent_session_id is supplied.
   */
  async createRecurringSessions(sessionInput: CreateRecurringSessionsInput): Promise<SessionData[]> {
    try {
      const {
        occurrences,
        parent_session_id,
        recurring_pattern,
        session_details,
        meeting_session,
        create_teams_meeting,
        participants: participantInput,
        ...baseInput
      } = sessionInput;
      const scheduledOccurrences = occurrences?.length ? occurrences : [sessionInput.scheduled_at];
      const participants = this.resolveSessionParticipants({
        ...baseInput,
        participants: participantInput
      });

      const sessions: SessionData[] = [];
      let parentSessionId = parent_session_id || null;

      for (const [index, scheduledAt] of scheduledOccurrences.entries()) {
        const sessionPayload = this.buildSessionInsertPayload({
          ...baseInput,
          scheduled_at: scheduledAt,
          scheduling_type: 'recurring',
          recurring_pattern,
          parent_session_id: parentSessionId
        });

        const { data, error } = await supabase
          .from('sessions')
          .insert(sessionPayload)
          .select('id')
          .single();

        if (error) {
          console.error('Error creating recurring session:', error);
          throw new Error(`Failed to create recurring session: ${error.message}`);
        }

        if (!parentSessionId && index === 0) {
          parentSessionId = data.id;
        }

        await this.insertSessionParticipants(data.id, participants);

        if (session_details || meeting_session) {
          await this.insertSessionRelatedRecords(data.id, session_details, meeting_session);
        }

        const createdSession = create_teams_meeting && !meeting_session
          ? await this.provisionTeamsMeetingForNewSession(data.id)
          : await this.fetchSessionOrThrow(data.id);

        sessions.push(createdSession);
      }

      return sessions;
    } catch (error) {
      console.error('Error in createRecurringSessions:', error);
      throw error;
    }
  }

  /**
   * Do not leave a usable "scheduled" session without its meeting. The record
   * remains available for administrators to correct, but cannot be joined.
   */
  private async provisionTeamsMeetingForNewSession(sessionId: string): Promise<SessionData> {
    try {
      return await this.createTeamsMeetingForSession(sessionId);
    } catch (error) {
      await supabase
        .from('sessions')
        .update({ status: 'failed' })
        .eq('id', sessionId);

      throw new Error(
        `The session could not be scheduled because its Teams meeting could not be provisioned: ${
          error instanceof Error ? error.message : 'Unknown Teams error'
        }`
      );
    }
  }

  /**
   * Fetch sessions for admin workflows with all related data.
   */
  async fetchAdminSessions(filters: FetchAdminSessionsFilters = {}): Promise<SessionData[]> {
    try {
      let query = supabase
        .from('sessions')
        .select(this.sessionSelect)
        .order('scheduled_at', { ascending: false });

      if (filters.status) query = query.eq('status', filters.status);
      if (filters.scheduling_type) query = query.eq('scheduling_type', filters.scheduling_type);
      if (filters.approval_status) query = query.eq('approval_status', filters.approval_status);
      if (filters.tutor_id) query = query.eq('tutor_id', filters.tutor_id);
      if (filters.student_id) query = query.eq('student_id', filters.student_id);
      if (filters.batch_id) query = query.eq('batch_id', filters.batch_id);
      if (filters.from) query = query.gte('scheduled_at', filters.from);
      if (filters.to) query = query.lte('scheduled_at', filters.to);
      if (filters.limit) query = query.limit(filters.limit);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching admin sessions:', error);
        throw new Error(`Failed to fetch admin sessions: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error in fetchAdminSessions:', error);
      throw error;
    }
  }

  /**
   * Update a session and optional related records.
   */
  async updateSession(sessionId: string, updates: UpdateSessionInput): Promise<SessionData> {
    try {
      const {
        session_details,
        meeting_session,
        create_teams_meeting,
        ...sessionUpdates
      } = updates;

      if (Object.keys(sessionUpdates).length > 0) {
        const { error } = await supabase
          .from('sessions')
          .update(sessionUpdates)
          .eq('id', sessionId);

        if (error) {
          console.error('Error updating session:', error);
          throw new Error(`Failed to update session: ${error.message}`);
        }
      }

      await this.insertSessionRelatedRecords(sessionId, session_details, meeting_session);

      return this.fetchSessionOrThrow(sessionId);
    } catch (error) {
      console.error('Error in updateSession:', error);
      throw error;
    }
  }

  /**
   * Cancel a session while preserving the cancellation audit fields.
   */
  async cancelSession(
    sessionId: string,
    options: {
      cancellation_reason?: string;
      last_updated_by?: string;
    } = {}
  ): Promise<SessionData> {
    return this.updateSession(sessionId, {
      status: 'cancelled',
      cancellation_reason: options.cancellation_reason,
      last_updated_by: options.last_updated_by,
      session_details: options.cancellation_reason
        ? { cancellation_reason: options.cancellation_reason }
        : undefined
    });
  }


  /**
   * Fetch sessions for a student with all related data
   */
  async fetchStudentSessions(studentId: string, status?: string): Promise<SessionData[]> {
    try {
      let query = supabase
        .from('sessions')
        .select(`
          *,
          tutor_profile:profiles!tutor_id(
            first_name,
            last_name,
            profile_picture_url,
            email,
            timezone
          ),
          student_profile:profiles!student_id(
            first_name,
            last_name,
            profile_picture_url,
            email
          ),
          curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
            name,
            domain:curriculum_domains(
              name,
              subject:subjects(
                name,
                description,
                icon
              )
            )
          ),
          curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
            title,
            learning_objective
          ),
          batch:batches(
            name,
            product_name
          ),
          meeting_session:meeting_sessions(
            meeting_url,
            join_url,
            meeting_status,
            recording_enabled,
            provider_meeting_id
          ),
          meeting_provider:meeting_providers(
            name,
            display_name
          ),
          session_feedback(
            rating,
            comments
          )
        `)
        .eq('student_id', studentId)
        .order('scheduled_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching sessions:', error);
        throw new Error(`Failed to fetch sessions: ${error.message}`);
      }

      // Now fetch tutor data for each session
      const sessionsWithTutorData = await Promise.all(
        (data || []).map(async (session) => {
          let tutorData = null;

          if (session.tutor_id) {
            // Try to fetch from tutors table first
            const { data: tutorTableData } = await supabase
              .from('tutors')
              .select(`
                education,
                hourly_rate,
                subjects_taught,
                experience,
                bio,
                verification_status,
                rating
              `)
              .eq('id', session.tutor_id)
              .single();

            // If not found in tutors table, try candidate_tutor table
            if (!tutorTableData) {
              const { data: candidateData } = await supabase
                .from('candidate_tutor')
                .select(`
                  education,
                  hourly_rate,
                  subjects_taught,
                  experience,
                  bio,
                  verification_status,
                  rating
                `)
                .eq('id', session.tutor_id)
                .single();

              tutorData = candidateData;
            } else {
              tutorData = tutorTableData;
            }
          }

          return {
            ...session,
            tutor_data: tutorData
          };
        })
      );

      return sessionsWithTutorData;
    } catch (error) {
      console.error('Error in fetchStudentSessions:', error);
      throw error;
    }
  }

  /**
   * Fetch upcoming sessions for a student
   */
  async fetchUpcomingSessions(studentId: string): Promise<SessionData[]> {
    try {
      // Calculate cutoff time: current time minus 60 minutes
      // This allows students to join sessions that started up to 60 minutes ago
      const cutoffTime = new Date();
      cutoffTime.setMinutes(cutoffTime.getMinutes() - 60);
      
      console.log('Fetching upcoming sessions with cutoff time:', cutoffTime.toISOString());
      
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          tutor_profile:profiles!tutor_id(
            first_name,
            last_name,
            profile_picture_url,
            email,
            timezone
          ),
          curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
            name,
            domain:curriculum_domains(
              name,
              subject:subjects(
                name,
                description,
                icon
              )
            )
          ),
          curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
            title,
            learning_objective
          ),
          batch:batches(
            name,
            product_name
          ),
          meeting_session:meeting_sessions(
            meeting_url,
            join_url,
            meeting_status,
            recording_enabled,
            provider_meeting_id
          ),
          meeting_provider:meeting_providers(
            name,
            display_name
          )
        `)
        .eq('student_id', studentId)
        .in('status', ['scheduled', 'started'])
        .gte('scheduled_at', cutoffTime.toISOString())
        .order('scheduled_at', { ascending: true });

      if (error) {
        console.error('Error fetching upcoming sessions:', error);
        throw new Error(`Failed to fetch upcoming sessions: ${error.message}`);
      }

      // Now fetch tutor profile and tutor data for each session
      const sessionsWithTutorData = await Promise.all(
        (data || []).map(async (session) => {
          let tutorProfile = null;
          let tutorData = null;

          if (session.tutor_id) {


            // Fetch tutor profile
            // Note: This may fail due to RLS policies. To fix, add comprehensive RLS policy to profiles table
            // that allows students and tutors to view each other's profiles at all assignment levels:
            // - Session level (sessions.tutor_id)
            // - Batch level (batches.default_tutor_id)
            // - Topic level (batch_topics.custom_tutor_id)
            // - Subtopic level (batch_subtopics.custom_tutor_id)
            // See full RLS policy in documentation.
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select(`
                first_name,
                last_name,
                profile_picture_url,
                email,
                timezone
              `)
              .eq('id', session.tutor_id)
              .single();

            if (profileError) {
              if (profileError.code === '42P17') {
                console.error('RLS policy causing infinite recursion. Please fix the RLS policy on profiles table.');
              } else {
                console.warn(`RLS policy blocking access to tutor profile ${session.tutor_id}.`);
              }

              // Since RLS is blocking profile access, we'll create a fallback profile
              tutorProfile = {
                first_name: 'Tutor',
                last_name: '',
                profile_picture_url: null,
                email: null,
                timezone: null
              };
            } else {
              tutorProfile = profileData;
            }

            // Try to fetch from tutors table first
            const { data: tutorTableData } = await supabase
              .from('tutors')
              .select(`
                education,
                hourly_rate,
                subjects_taught,
                experience,
                bio,
                verification_status,
                rating
              `)
              .eq('id', session.tutor_id)
              .single();

            // If not found in tutors table, try candidate_tutor table
            if (!tutorTableData) {
              const { data: candidateData } = await supabase
                .from('candidate_tutor')
                .select(`
                  education,
                  hourly_rate,
                  subjects_taught,
                  experience,
                  bio,
                  verification_status,
                  rating
                `)
                .eq('id', session.tutor_id)
                .single();

              tutorData = candidateData;
            } else {
              tutorData = tutorTableData;
            }
          }

          // If we have tutor data but no profile (due to RLS), try to enhance the fallback profile
          if (tutorProfile && tutorProfile.first_name === 'Tutor' && tutorData) {
            // Check if tutor data has any name information we can use
            if (tutorData.bio && tutorData.bio.includes('I am ')) {
              // Try to extract name from bio if it follows a pattern like "I am John Doe"
              const nameMatch = tutorData.bio.match(/I am ([A-Za-z\s]+)/);
              if (nameMatch) {
                const fullName = nameMatch[1].trim();
                const nameParts = fullName.split(' ');
                tutorProfile.first_name = nameParts[0] || 'Tutor';
                tutorProfile.last_name = nameParts.slice(1).join(' ') || '';
              }
            }
          }

          return {
            ...session,
            tutor_profile: tutorProfile,
            tutor_data: tutorData
          };
        })
      );

      return sessionsWithTutorData;
    } catch (error) {
      console.error('Error in fetchUpcomingSessions:', error);
      throw error;
    }
  }

  /**
   * Fetch past sessions for a student
   */
  async fetchPastSessions(studentId: string): Promise<SessionData[]> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          tutor_profile:profiles!tutor_id(
            first_name,
            last_name,
            profile_picture_url,
            email,
            timezone
          ),
          curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
            name,
            domain:curriculum_domains(
              name,
              subject:subjects(
                name,
                description,
                icon
              )
            )
          ),
          curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
            title,
            learning_objective
          ),
          batch:batches(
            name,
            product_name
          ),
          session_feedback(
            rating,
            comments
          ),
          session_details(
            participation_score,
            participation_level,
            notes,
            materials,
            student_joined_at,
            student_left_at,
            tutor_joined_at,
            tutor_left_at
          )
        `)
        .eq('student_id', studentId)
        .eq('status', 'ended')
        .order('scheduled_at', { ascending: false });

      if (error) {
        console.error('Error fetching past sessions:', error);
        throw new Error(`Failed to fetch past sessions: ${error.message}`);
      }

      // Now fetch tutor data for each session
      const sessionsWithTutorData = await Promise.all(
        (data || []).map(async (session) => {
          let tutorData = null;

          if (session.tutor_id) {
            // Try to fetch from tutors table first
            const { data: tutorTableData } = await supabase
              .from('tutors')
              .select(`
                education,
                hourly_rate,
                subjects_taught,
                experience,
                bio,
                verification_status,
                rating
              `)
              .eq('id', session.tutor_id)
              .single();

            // If not found in tutors table, try candidate_tutor table
            if (!tutorTableData) {
              const { data: candidateData } = await supabase
                .from('candidate_tutor')
                .select(`
                  education,
                  hourly_rate,
                  subjects_taught,
                  experience,
                  bio,
                  verification_status,
                  rating
                `)
                .eq('id', session.tutor_id)
                .single();

              tutorData = candidateData;
            } else {
              tutorData = tutorTableData;
            }
          }

          return {
            ...session,
            tutor_data: tutorData
          };
        })
      );

      return sessionsWithTutorData;
    } catch (error) {
      console.error('Error in fetchPastSessions:', error);
      throw error;
    }
  }

  /**
   * Calculate session statistics for a student
   */
  async calculateSessionStats(studentId: string): Promise<SessionStats> {
    try {
      const allSessions = await this.fetchStudentSessions(studentId);
      
      // Calculate cutoff time: current time minus 60 minutes
      // This allows students to join sessions that started up to 60 minutes ago
      const cutoffTime = new Date();
      cutoffTime.setMinutes(cutoffTime.getMinutes() - 60);
      
      const totalSessions = allSessions.length;
      const completedSessions = allSessions.filter(s => s.status === 'ended').length;
      const upcomingSessions = allSessions.filter(s => (s.status === 'scheduled' || s.status === 'started') && new Date(s.scheduled_at) > cutoffTime).length;
      
      // Calculate total hours
      const totalHours = allSessions.reduce((total, session) => {
        return total + (session.duration_min / 60);
      }, 0);

      // Calculate average rating
      const ratingsData = allSessions
        .filter(s => s.session_feedback && s.session_feedback.length > 0)
        .flatMap(s => s.session_feedback?.filter(f => f.rating) || []);
      
      const averageRating = ratingsData.length > 0 
        ? ratingsData.reduce((sum, feedback) => sum + (feedback.rating || 0), 0) / ratingsData.length
        : 0;

      // Get favorite topics (most frequent)
      const topicCounts = allSessions.reduce((acc, session) => {
        const topicName = session.curriculum_unit?.name || 'Unassigned unit';
        acc[topicName] = (acc[topicName] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const favoriteTopics = Object.entries(topicCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([topic]) => topic);

      // Get favorite tutors (most frequent)
      const tutorCounts = allSessions.reduce((acc, session) => {
        const tutorName = session.tutor_profile 
          ? `${session.tutor_profile.first_name} ${session.tutor_profile.last_name}`
          : 'Unknown Tutor';
        acc[tutorName] = (acc[tutorName] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const favoriteTutors = Object.entries(tutorCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([tutor]) => tutor);

      return {
        totalSessions,
        totalHours: Math.round(totalHours * 10) / 10, // Round to 1 decimal place
        completedSessions,
        upcomingSessions,
        averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
        favoriteTopics,
        favoriteTutors
      };
    } catch (error) {
      console.error('Error calculating session stats:', error);
      throw error;
    }
  }

  /**
   * Fetch sessions for a tutor with all related data
   */
  async fetchTutorSessions(tutorId: string, status?: string): Promise<SessionData[]> {
    try {
      let query = supabase
        .from('sessions')
        .select(`
          *,
          tutor_profile:profiles!tutor_id(
            first_name,
            last_name,
            profile_picture_url,
            email,
            timezone
          ),
          student_profile:profiles!student_id(
            first_name,
            last_name,
            profile_picture_url,
            email
          ),
          curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
            name,
            domain:curriculum_domains(
              name,
              subject:subjects(
                name,
                description,
                icon
              )
            )
          ),
          curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
            title,
            learning_objective
          ),
          batch:batches(
            name,
            product_name
          ),
          meeting_session:meeting_sessions(
            meeting_url,
            join_url,
            meeting_status,
            recording_enabled,
            provider_meeting_id
          ),
          meeting_provider:meeting_providers(
            name,
            display_name
          ),
          session_feedback(
            rating,
            comments
          )
        `)
        .eq('tutor_id', tutorId)
        .order('scheduled_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching tutor sessions:', error);
        throw new Error(`Failed to fetch tutor sessions: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error in fetchTutorSessions:', error);
      throw error;
    }
  }

  /**
   * Fetch upcoming sessions for a tutor
   */
  async fetchTutorUpcomingSessions(tutorId: string): Promise<SessionData[]> {
    try {
      // Calculate cutoff time: current time minus 60 minutes
      // This allows tutors to join sessions that started up to 60 minutes ago
      const cutoffTime = new Date();
      cutoffTime.setMinutes(cutoffTime.getMinutes() - 60);
      
      console.log('Fetching tutor upcoming sessions with cutoff time:', cutoffTime.toISOString());
      
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          student_profile:profiles!student_id(
            first_name,
            last_name,
            profile_picture_url,
            email
          ),
          curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
            name,
            domain:curriculum_domains(
              name,
              subject:subjects(
                name,
                description,
                icon
              )
            )
          ),
          curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
            title,
            learning_objective
          ),
          batch:batches(
            name,
            product_name
          ),
          meeting_session:meeting_sessions(
            meeting_url,
            join_url,
            meeting_status,
            recording_enabled,
            provider_meeting_id
          ),
          meeting_provider:meeting_providers(
            name,
            display_name
          )
        `)
        .eq('tutor_id', tutorId)
        .in('status', ['scheduled', 'started'])
        .gte('scheduled_at', cutoffTime.toISOString())
        .order('scheduled_at', { ascending: true });

      if (error) {
        console.error('Error fetching tutor upcoming sessions:', error);
        throw new Error(`Failed to fetch tutor upcoming sessions: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error in fetchTutorUpcomingSessions:', error);
      throw error;
    }
  }

  /**
   * Fetch past sessions for a tutor
   */
  async fetchTutorPastSessions(tutorId: string): Promise<SessionData[]> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          student_profile:profiles!student_id(
            first_name,
            last_name,
            profile_picture_url,
            email
          ),
          curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
            name,
            domain:curriculum_domains(
              name,
              subject:subjects(
                name,
                description,
                icon
              )
            )
          ),
          curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
            title,
            learning_objective
          ),
          batch:batches(
            name,
            product_name
          ),
          session_feedback(
            rating,
            comments
          )
        `)
        .eq('tutor_id', tutorId)
        .eq('status', 'ended')
        .order('scheduled_at', { ascending: false });

      if (error) {
        console.error('Error fetching tutor past sessions:', error);
        throw new Error(`Failed to fetch tutor past sessions: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error in fetchTutorPastSessions:', error);
      throw error;
    }
  }

  /**
   * Fetch sessions for a tutor on a specific date
   */
  async fetchTutorSessionsForDate(tutorId: string, date: Date): Promise<SessionData[]> {
    try {
      // Get start and end of the selected date
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      console.log('Fetching tutor sessions for date range:', startOfDay.toISOString(), 'to', endOfDay.toISOString());
      
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          student_profile:profiles!student_id(
            first_name,
            last_name,
            profile_picture_url,
            email
          ),
          curriculum_unit:curriculum_units!sessions_curriculum_unit_id_fkey(
            name,
            domain:curriculum_domains(
              name,
              subject:subjects(
                name,
                description,
                icon
              )
            )
          ),
          curriculum_objective:curriculum_objectives!sessions_curriculum_objective_id_fkey(
            title,
            learning_objective
          ),
          batch:batches(
            name,
            product_name
          ),
          meeting_session:meeting_sessions(
            meeting_url,
            join_url,
            meeting_status,
            recording_enabled,
            provider_meeting_id
          ),
          meeting_provider:meeting_providers(
            name,
            display_name
          )
        `)
        .eq('tutor_id', tutorId)
        .gte('scheduled_at', startOfDay.toISOString())
        .lte('scheduled_at', endOfDay.toISOString())
        .order('scheduled_at', { ascending: true });

      if (error) {
        console.error('Error fetching tutor sessions for date:', error);
        throw new Error(`Failed to fetch tutor sessions for date: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error in fetchTutorSessionsForDate:', error);
      throw error;
    }
  }

  /**
   * Calculate session statistics for a tutor
   */
  async calculateTutorSessionStats(tutorId: string): Promise<SessionStats> {
    try {
      const allSessions = await this.fetchTutorSessions(tutorId);
      
      // Calculate cutoff time: current time minus 60 minutes
      const cutoffTime = new Date();
      cutoffTime.setMinutes(cutoffTime.getMinutes() - 60);
      
      const totalSessions = allSessions.length;
      const completedSessions = allSessions.filter(s => s.status === 'ended').length;
      const upcomingSessions = allSessions.filter(s => (s.status === 'scheduled' || s.status === 'started') && new Date(s.scheduled_at) > cutoffTime).length;
      
      // Calculate total hours
      const totalHours = allSessions.reduce((total, session) => {
        return total + (session.duration_min / 60);
      }, 0);

      // Calculate average rating from session feedback
      const ratingsData = allSessions
        .filter(s => s.session_feedback && s.session_feedback.length > 0)
        .flatMap(s => s.session_feedback?.filter(f => f.rating) || []);
      
      const averageRating = ratingsData.length > 0 
        ? ratingsData.reduce((sum, feedback) => sum + (feedback.rating || 0), 0) / ratingsData.length
        : 0;

      // Get favorite topics (most taught)
      const topicCounts = allSessions.reduce((acc, session) => {
        const topicName = session.curriculum_unit?.name || 'Unassigned unit';
        acc[topicName] = (acc[topicName] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const favoriteTopics = Object.entries(topicCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([topic]) => topic);

      // Get favorite students (most frequent)
      const studentCounts = allSessions.reduce((acc, session) => {
        const studentName = session.student_profile 
          ? `${session.student_profile.first_name} ${session.student_profile.last_name}`
          : 'Unknown Student';
        acc[studentName] = (acc[studentName] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const favoriteTutors = Object.entries(studentCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([student]) => student);

      return {
        totalSessions,
        totalHours: Math.round(totalHours * 10) / 10,
        completedSessions,
        upcomingSessions,
        averageRating: Math.round(averageRating * 10) / 10,
        favoriteTopics,
        favoriteTutors // This will actually contain favorite students for tutors
      };
    } catch (error) {
      console.error('Error calculating tutor session stats:', error);
      throw error;
    }
  }

  /**
   * Get session by ID with full details
   */
  async getSessionById(sessionId: string): Promise<SessionData | null> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(this.sessionSelect)
        .eq('id', sessionId)
        .single();

      if (error) {
        console.error('Error fetching session by ID:', error);
        return null;
      }

      // Fetch tutor data
      let tutorData = null;
      if (data.tutor_id) {
        // Try to fetch from tutors table first
        const { data: tutorTableData } = await supabase
          .from('tutors')
          .select(`
            education,
            hourly_rate,
            subjects_taught,
            experience,
            bio,
            verification_status,
            rating
          `)
          .eq('id', data.tutor_id)
          .single();

        // If not found in tutors table, try candidate_tutor table
        if (!tutorTableData) {
          const { data: candidateData } = await supabase
            .from('candidate_tutor')
            .select(`
              education,
              hourly_rate,
              subjects_taught,
              experience,
              bio,
              verification_status,
              rating
            `)
            .eq('id', data.tutor_id)
            .single();

          tutorData = candidateData;
        } else {
          tutorData = tutorTableData;
        }
      }

      return {
        ...data,
        tutor_data: tutorData
      };
    } catch (error) {
      console.error('Error in getSessionById:', error);
      return null;
    }
  }
}

export const sessionsService = new SessionsService();
